import { useCallback } from 'react';
import { useNotify } from 'react-admin';
import { FILE_STATUS } from './useLeadImportState';

/**
 * Custom hook for file validation logic
 */
export const useFileValidation = (setFileStatus, setCsvFile) => {
  const notify = useNotify();

  const notificationOpts = {
    type: 'error',
    autoHideDuration: 60000,
    messageArgs: { _: { persistent: true } },
  };

  const validateFileType = useCallback((file) => {
    if (!file) {
      setFileStatus(FILE_STATUS.REMOVED);
      setCsvFile(null);
      return true; // Allow clearing the file
    }

    // Check file size (300MB = 300 * 1024 * 1024 bytes)
    const maxSize = 300 * 1024 * 1024;
    if (file.size > maxSize) {
      setFileStatus(FILE_STATUS.REJECTED_FILE_TYPE);
      notify('File size too large. Please upload a file smaller than 300MB.', {
        ...notificationOpts,
        messageArgs: { _: { persistent: true } },
      });
      return false;
    }

    // Accept CSV and Excel files
    const supportedTypes = [
      'text/csv',
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.openxmlformats-officedocument.spreadsheetml.template', // .xltx
      'application/vnd.ms-excel.sheet.macroEnabled.12', // .xlsm
      'application/vnd.ms-excel.template.macroEnabled.12', // .xltm
    ];

    const supportedExtensions = ['.csv', '.xlsx', '.xls', '.xltx', '.xlsm', '.xltm'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    const isValidType = supportedTypes.includes(file.type) || supportedExtensions.includes(fileExtension);

    if (!isValidType) {
      setFileStatus(FILE_STATUS.REJECTED_FILE_TYPE);
      notify('Invalid file type. Please upload a CSV or Excel (.xlsx, .xlsm, .xltx, .xltm) file.', {
        ...notificationOpts,
        messageArgs: { _: { persistent: true } },
      });
      return false;
    }

    return true;
  }, [setFileStatus, setCsvFile, notify, notificationOpts]);

  const handleFileSelect = useCallback((fileInput) => {
    const file = fileInput?.file || fileInput;

    if (validateFileType(file)) {
      // If file is null, validateFileType already handled setting the state correctly
      // (status = REMOVED, csvFile = null), so we don't need to do anything else
      if (file) {
        setFileStatus(FILE_STATUS.PREPARING);
        setCsvFile(file);
      }
    }
  }, [validateFileType, setFileStatus, setCsvFile]);

  return {
    validateFileType,
    handleFileSelect,
  };
};
